/**
 * **The combat glossary** (`projects/combat-glossary.md`, `decisions/0186` item 5): one lookup that
 * answers every term a player sees on the combat surface with its plain-language rule. `<Term>`
 * (`./Term.tsx`) renders an entry; this module only resolves one.
 *
 * Statuses, tags and action names already have their rules text elsewhere, so their keys delegate
 * and nothing is copied: `status:` reads {@link statusBlurb}, `tag:` reads the compendium through
 * {@link tagRuleText}, `action:` reads the core action tables' blurbs (`MECH_ACTIONS` first, then
 * `PILOT_ACTIONS` and `NPC_ACTIONS`). A granted action has no key here: its host passes its own
 * compendium text as `<Term>`'s `fallback`.
 *
 * Action types, stats and roll terms had no rule text anywhere, so {@link GLOSSARY_LINES} writes
 * them, each from the Core book page it cites. A mechanic too big for one line gets a `card:` key
 * instead, written in {@link GLOSSARY_CARDS} the same way. They are rules claims, registered as a copy source in
 * `rulesCopyRegistry.ts`: check any edit against the book, not against a prototype or a comment.
 */
import type { Compendium } from "@lancer-vtt/compendium";
import { MECH_ACTIONS, NPC_ACTIONS, PILOT_ACTIONS } from "@lancer-vtt/rules";
import { tagRuleText } from "../encounter/commandMenu";
import { statusBlurb } from "../encounter/statusCatalog";
import { formatTagChip } from "../encounter/weaponFormat";

export const ACTION_TYPE_IDS = ["actions", "move", "protocol", "quick", "full", "free", "reaction", "overcharge"] as const;
export const STAT_IDS = ["hp", "heat", "structure", "stress", "evasion", "edef", "armor", "speed", "sensors", "save_target", "size", "repair_cap", "tech_attack", "grit"] as const;
export const ROLL_IDS = ["accuracy", "difficulty", "threat", "range", "mount"] as const;
export const CARD_IDS = ["heat"] as const;

export type ActionTypeId = (typeof ACTION_TYPE_IDS)[number];
export type StatId = (typeof STAT_IDS)[number];
export type RollId = (typeof ROLL_IDS)[number];
export type CardId = (typeof CARD_IDS)[number];

/** A term's key: its family, then its id within that family. */
export type GlossaryKey = `status:${string}` | `tag:${string}` | `action:${string}` | `type:${ActionTypeId}` | `stat:${StatId}` | `roll:${RollId}` | `card:${CardId}`;

/** What a popover shows. `text` is rules prose, possibly lancer-data HTML (render it with `RulesText`). */
export type GlossaryEntry = { title: string; kind: string; text: string };

export type GlossaryCtx = {
  /** Resolves `tag:` keys. Without it they answer nothing. */
  compendium?: Pick<Compendium, "getById">;
  /** A `tag:` key's `{VAL}`, e.g. 3 for Reliable 3. */
  val?: number;
};

/** One written line: its title, the kind a popover labels it with, the rule, and the Core page it comes from. */
export type GlossaryLine = { title: string; kind: string; text: string; page: string };

/**
 * The action-type, stat and roll lines. Every `text` is a rules claim written from
 * `reference/lancer-core-book.txt` at the cited page; the numbers come from the book too
 * (`decisions/0122`). Difficulty subtracts the HIGHEST of the d6s rolled, never a flat amount, and
 * every threshold here is "equals or exceeds".
 */
export const GLOSSARY_LINES: {
  type: Readonly<Record<ActionTypeId, GlossaryLine>>;
  stat: Readonly<Record<StatId, GlossaryLine>>;
  roll: Readonly<Record<RollId, GlossaryLine>>;
} = {
  type: {
    actions: {
      title: "Actions",
      kind: "Turn",
      text: "Each turn you get two actions, shown as diamonds: a Quick action spends one and a Full action spends both. Free actions, Protocols and reactions spend none, and Overcharge takes one more Quick action for heat.",
      page: "Core p.61, p.73",
    },
    move: {
      title: "Standard move",
      kind: "Movement",
      text: "Once per turn, move up to your Speed in spaces. A Boost is a Quick action that moves you up to your Speed again.",
      page: "Core p.34, p.61",
    },
    protocol: {
      title: "Protocol",
      kind: "Action type",
      text: "A free action you can take only at the start of your turn, before you move or take any other action. Each Protocol can be taken once per turn.",
      page: "Core p.73",
    },
    quick: {
      title: "Quick action",
      kind: "Action type",
      text: "An action that takes a few moments, like firing a weapon or activating a system. Each turn you get a standard move plus either two Quick actions or one Full action, and most actions can't be taken twice in one turn.",
      page: "Core p.61",
    },
    full: {
      title: "Full action",
      kind: "Action type",
      text: "An action that needs your full attention, like a Barrage or field repairs. It takes the place of both of your turn's Quick actions.",
      page: "Core p.61",
    },
    free: {
      title: "Free action",
      kind: "Action type",
      text: "Granted by systems, talents, gear or Overcharge. Take any number of them, but only on your turn. They don't count toward your Quick or Full actions, and they can repeat an action you already took this turn.",
      page: "Core p.61, p.73",
    },
    reaction: {
      title: "Reaction",
      kind: "Action type",
      text: "Taken in response to its trigger, even on another character's turn. You can take one reaction per turn, yours or anyone else's. A reaction you use can't be used again until your own next turn starts. Every mech can Brace and Overwatch.",
      page: "Core p.73",
    },
    overcharge: {
      title: "Overcharge",
      kind: "Action type",
      text: "Once per turn, take any Quick action as a free action, even one you already took this turn. It costs heat: 1 the first time, 1d3 the second, 1d6 the third, and 1d6+4 every time after. A Full Repair resets the count.",
      page: "Core p.73",
    },
  },
  stat: {
    hp: {
      title: "HP",
      kind: "Stat",
      text: "Hit points. Damage comes off HP. When a mech reaches 0 HP it takes 1 structure damage, makes a structure damage check, and its HP resets to full.",
      page: "Core p.34, p.80",
    },
    heat: {
      title: "Heat",
      kind: "Stat",
      text: "Harm to the mech's internals, from its own systems and from enemy tech. It fills your Heat Cap. When a mech takes more heat than its Heat Cap, it takes 1 stress damage, clears all heat, and makes an overheating check.",
      page: "Core p.34, p.78",
    },
    structure: {
      title: "Structure",
      kind: "Stat",
      text: "How many times a mech can be knocked to 0 HP. Each time, it takes 1 structure damage and rolls 1d6 per point of structure damage marked, reading the lowest die on the structure damage table. Most mechs have 4 and are destroyed at 0.",
      page: "Core p.34, p.80",
    },
    stress: {
      title: "Stress",
      kind: "Stat",
      text: "Reactor stress. Each time heat goes past the Heat Cap, the mech takes 1 stress damage and rolls 1d6 per point of stress damage marked, reading the lowest die on the overheating table. Most mechs have 4; at 0 the reactor melts down.",
      page: "Core p.34, p.81",
    },
    evasion: {
      title: "Evasion",
      kind: "Defense",
      text: "How hard it is to hit you with melee and ranged attacks. An attack hits when its total equals or exceeds your Evasion.",
      page: "Core p.13, p.34",
    },
    edef: {
      title: "E-Defense",
      kind: "Defense",
      text: "How hard it is to hit you with tech attacks. A tech attack hits when its total equals or exceeds your E-Defense.",
      page: "Core p.34, p.64",
    },
    armor: {
      title: "Armor",
      kind: "Defense",
      text: "Reduces all kinetic, energy and explosive damage you take by this amount. Armor-piercing (AP) damage ignores it. A mech never has more than 4.",
      page: "Core p.32, p.78",
    },
    speed: {
      title: "Speed",
      kind: "Movement",
      text: "How many spaces you can move with a standard move or a Boost.",
      page: "Core p.34, p.79",
    },
    sensors: {
      title: "Sensors",
      kind: "Range",
      text: "The farthest you can detect other characters, make tech attacks, Lock On and use some systems, in spaces. You know where a character inside your Sensors is, even out of sight, unless it is Hidden.",
      page: "Core p.34, p.79",
    },
    save_target: {
      title: "Save Target",
      kind: "Stat",
      text: "When you force another character to make a save, they must match or beat your Save Target or take the consequences. It is not your own save bonus.",
      page: "Core p.34",
    },
    size: {
      title: "Size",
      kind: "Stat",
      text: "How many spaces a character fills on each side: a Size 2 mech fills a 2×2 area. Size 1/2 fills one space, like Size 1. Most mechs are Size 1.",
      page: "Core p.32, p.79",
    },
    repair_cap: {
      title: "Repair Cap",
      kind: "Stat",
      text: "How many repairs the mech can take per mission, spent to heal it and fix damaged systems. Out of repairs, it can no longer regain HP or fix systems in the field.",
      page: "Core p.34, p.78",
    },
    tech_attack: {
      title: "Tech Attack",
      kind: "Stat",
      text: "Your bonus on tech attack rolls, added in place of Grit, and the stat most tech actions use.",
      page: "Core p.34, p.79",
    },
    grit: {
      title: "Grit",
      kind: "Stat",
      text: "Half your pilot's license level, rounded up. It adds to your mech's attack rolls, HP, Save Target and system points.",
      page: "Core p.14, p.34",
    },
  },
  roll: {
    accuracy: {
      title: "Accuracy",
      kind: "Roll",
      text: "Each point of Accuracy adds 1d6 to the roll. Roll them all and add only the highest die, so the most it can add is 6. Accuracy and Difficulty cancel one for one.",
      page: "Core p.13-14",
    },
    difficulty: {
      title: "Difficulty",
      kind: "Roll",
      text: "Each point of Difficulty rolls 1d6, and you subtract only the highest die from the roll, so the most it can take is 6. Accuracy and Difficulty cancel one for one.",
      page: "Core p.13-14",
    },
    threat: {
      title: "Threat",
      kind: "Range",
      text: "The farthest a melee weapon can attack, and the range at which any weapon can make Overwatch attacks, measured from the edge of your mech. A weapon has Threat 1 unless it says otherwise.",
      page: "Core p.64, p.79",
    },
    range: {
      title: "Range",
      kind: "Range",
      text: "The farthest a weapon can make ranged attacks, in spaces, measured from the attacker.",
      page: "Core p.64, p.78",
    },
    mount: {
      title: "Mount",
      kind: "Loadout",
      text: "Where a frame's weapons sit. A Main mount holds one Main or Auxiliary weapon; Heavy, one Heavy, Main or Auxiliary; Aux/Aux, two Auxiliary; Main/Aux, one Main and one Auxiliary or two Auxiliary; Flexible, one Main or two Auxiliary. A Skirmish can add an attack with a different Auxiliary weapon on the same mount, which deals no bonus damage.",
      page: "Core p.32, p.70",
    },
  },
};

/** One paragraph of a {@link GlossaryCard}: a bold lead, the rule, and the Core pages it comes from. */
export type GlossaryCardLine = { lead: string; text: string; page: string };

/** A mechanic too big for one line, explained in a few short paragraphs behind an (i). */
export type GlossaryCard = { title: string; kind: string; lines: readonly GlossaryCardLine[] };

/**
 * The explainer cards (`projects/heat-info-card.md`). Same contract as {@link GLOSSARY_LINES}: every
 * line is a rules claim written from `reference/lancer-core-book.txt` at the cited page, and a GM
 * reads the whole card mid-fight, so each line stays one or two sentences.
 */
export const GLOSSARY_CARDS: Readonly<Record<CardId, GlossaryCard>> = {
  heat: {
    title: "Heat",
    kind: "How heat works",
    lines: [
      {
        lead: "Gaining heat.",
        text: "A mech marks heat from its own gear (Heat (Self) weapons and systems, Overcharge) and from enemies (Heat (Target) hits, Invade's 2 heat). Heat is not damage and ignores Armor.",
        page: "Core p.67, p.70, p.73, p.104, p.106",
      },
      {
        lead: "Heat Cap.",
        text: "The most heat the mech can hold. Any heat past it overheats the mech.",
        page: "Core p.67",
      },
      {
        lead: "Danger Zone.",
        text: "Heat at half the Heat Cap or more. Some weapons, talents and effects work only in the Danger Zone.",
        page: "Core p.77, p.81, p.105",
      },
      {
        lead: "Overheating.",
        text: "The mech takes 1 stress and rolls 1d6 per stress damage marked, keeping the lowest die. Then it clears all heat and takes the excess, which can overheat it again. At 0 stress the reactor melts down at the end of its next turn.",
        page: "Core p.81",
      },
      {
        lead: "The roll.",
        text: "5-6: Impaired until the end of its next turn. 2-4: Exposed. 1, by stress left: 3+ stress: Exposed; 2 stress: an ENGINEERING check, Exposed on a pass, meltdown after 1d6 turns on a fail; 1 stress: meltdown at the end of its next turn. Two or more 1s: meltdown. Exposed doubles kinetic, explosive and energy damage taken.",
        page: "Core p.77, p.81",
      },
      {
        lead: "NPCs.",
        text: "An NPC with a Heat Cap has 1 stress unless its sheet says more, and past its cap it becomes Exposed instead of rolling. An NPC without a Heat Cap takes heat as energy damage.",
        page: "Core p.281",
      },
      {
        lead: "Shedding heat.",
        text: "Stabilize (Full action) can cool the mech, clearing all heat and Exposed. Shut Down (Quick action) clears both too, but the mech is Stunned until it Boots Up (Full action). Some systems cool, and rest or a Full Repair resets heat.",
        page: "Core p.71, p.72, p.81",
      },
    ],
  },
};

/** Capitalize a status blurb's fragment into a sentence, the way a popover reads it. */
function sentence(fragment: string): string {
  const t = fragment.trim();
  const cap = t.charAt(0).toUpperCase() + t.slice(1);
  return /[.!?]$/.test(cap) ? cap : `${cap}.`;
}

const COST_KIND: Readonly<Record<string, string>> = { Quick: "Quick action", Full: "Full action", Free: "Free action", Reaction: "Reaction", Protocol: "Protocol" };

function coreAction(id: string): GlossaryEntry | undefined {
  const a = [...MECH_ACTIONS, ...PILOT_ACTIONS, ...NPC_ACTIONS].find((x) => x.id === id);
  if (!a) return undefined;
  const cost = (a as { cost?: string }).cost;
  return { title: a.name, kind: (cost !== undefined ? COST_KIND[cost] : undefined) ?? "Action", text: a.blurb };
}

/** Resolve a term key to its entry, or `undefined` when nothing on file answers it (render the term plain). */
export function glossaryEntry(key: GlossaryKey, ctx: GlossaryCtx = {}): GlossaryEntry | undefined {
  const at = key.indexOf(":");
  const family = key.slice(0, at);
  const id = key.slice(at + 1);
  switch (family) {
    case "status": {
      const blurb = statusBlurb(id);
      return blurb === undefined ? undefined : { title: id, kind: "Status", text: sentence(blurb) };
    }
    case "tag": {
      if (!ctx.compendium) return undefined;
      const text = tagRuleText(ctx.compendium, id, ctx.val);
      return text === undefined ? undefined : { title: formatTagChip(id, ctx.val), kind: "Weapon tag", text };
    }
    case "action":
      return coreAction(id);
    case "type":
    case "stat":
    case "roll": {
      const line = (GLOSSARY_LINES[family] as Readonly<Record<string, GlossaryLine>>)[id];
      return line === undefined ? undefined : { title: line.title, kind: line.kind, text: line.text };
    }
    case "card": {
      const card = (GLOSSARY_CARDS as Readonly<Record<string, GlossaryCard>>)[id];
      return card === undefined ? undefined : { title: card.title, kind: card.kind, text: card.lines.map((l) => `<p><b>${l.lead}</b> ${l.text}</p>`).join("") };
    }
    default:
      return undefined;
  }
}
